import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BenefitsSection = () => {
  const [activeBenefit, setActiveBenefit] = useState(0);
  const [visibleBenefits, setVisibleBenefits] = useState([]);

  const benefits = [
  {
    id: 1,
    icon: 'Smartphone',
    title: 'تسوق سهل ومريح',
    shortDesc: 'واجهة بسيطة ومحسنة للجوال',
    detailedDesc: 'تطبيق وموقع محسن للجوال مع تجربة تسوق سلسة وآمنة. بحث ذكي، فلترة متقدمة، وعربة تسوق محفوظة.',
    image: "https://images.unsplash.com/photo-1726066012801-14d892021339",
    imageAlt: 'Person using smartphone to browse pharmacy app with easy navigation interface',
    features: ['بحث ذكي', 'فلترة متقدمة', 'عربة محفوظة', 'دفع آمن'],
    color: 'primary'
  },
  {
    id: 2,
    icon: 'Shield',
    title: 'منتجات أصلية مضمونة',
    shortDesc: 'ضمان الأصالة أو استرداد المال',
    detailedDesc: 'جميع منتجاتنا أصلية 100% مع شهادات الجودة. نعمل مباشرة مع الموردين المعتمدين وضمان استرداد المال.',
    image: "https://images.unsplash.com/photo-1539305504747-1bfedeae9b29",
    imageAlt: 'Authentic medicine packages with quality certificates and verification seals',
    features: ['شهادات جودة', 'موردين معتمدين', 'فحص دقيق', 'ضمان استرداد'],
    color: 'success'
  },
  {
    id: 3,
    icon: 'Truck',
    title: 'توصيل سريع وموثوق',
    shortDesc: 'خلال ساعتين في نفس اليوم',
    detailedDesc: 'شبكة توصيل متطورة تغطي جميع المناطق. تتبع مباشر للطلب، توصيل مجاني للطلبات فوق 200 ريال.',
    image: "https://images.unsplash.com/photo-1659353741017-aa112e7a854d",
    imageAlt: 'Delivery person with pharmacy packages providing fast and reliable service',
    features: ['تتبع مباشر', 'توصيل مجاني', 'تغطية شاملة', 'مواعيد مرنة'],
    color: 'accent'
  },
  {
    id: 4,
    icon: 'UserCheck',
    title: 'استشارات مهنية مجانية',
    shortDesc: 'صيادلة مرخصون متاحون دائماً',
    detailedDesc: 'فريق من الصيادلة المرخصين متاح للاستشارة المجانية. مكالمات فيديو، محادثة نصية، أو هاتفية حسب تفضيلك.',
    image: "https://images.unsplash.com/photo-1576091358783-a212ec293ff3",
    imageAlt: 'Licensed pharmacist providing professional consultation via video call to customer',
    features: ['مكالمات فيديو', 'محادثة نصية', 'استشارة هاتفية', 'متاح 24/7'],
    color: 'secondary'
  },
  {
    id: 5,
    icon: 'Gift',
    title: 'عروض وخصومات حصرية',
    shortDesc: 'وفر أكثر مع برنامج الولاء',
    detailedDesc: 'برنامج ولاء متقدم مع نقاط مكافآت، خصومات حصرية، وعروض شخصية. كلما تسوقت أكثر، وفرت أكثر.',
    image: "https://images.unsplash.com/photo-1716415529218-3b88cf1ae3a2",
    imageAlt: 'Gift boxes and discount coupons representing exclusive offers and loyalty rewards',
    features: ['نقاط مكافآت', 'خصومات حصرية', 'عروض شخصية', 'هدايا مجانية'],
    color: 'warning'
  }];


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const benefitId = parseInt(entry.target.dataset.benefitId);
            setVisibleBenefits((prev) => [...new Set([...prev, benefitId])]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const benefitElements = document.querySelectorAll('[data-benefit-id]');
    benefitElements?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveBenefit((prev) => (prev + 1) % benefits?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleBenefitClick = (index) => {
    setActiveBenefit(index);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white via-muted/30 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Icon name="Star" size={20} />
            <span className="font-cairo font-semibold">مميزات حصرية</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-noto-arabic font-bold text-text-primary mb-6">
            لماذا تختار روما؟
          </h2>
          
          <p className="text-lg text-text-secondary font-cairo max-w-3xl mx-auto leading-relaxed">
            نحن لا نقدم منتجات فقط، بل تجربة متكاملة تجمع بين الجودة والراحة والثقة. 
            اكتشف المميزات التي تجعلنا الخيار الأول لآلاف العملاء.
          </p>
        </div>

        {/* Interactive Benefits Showcase */}
        <div className="grid lg:grid-cols-1 gap-12 items-center mb-16">
          {/* Benefits List 
          <div className="space-y-4 lg:order-2">
            {benefits?.map((benefit, index) =>
            <div
              key={benefit?.id}
              data-benefit-id={benefit?.id}
              onClick={() => handleBenefitClick(index)}
              className={`cursor-pointer transition-all duration-500 ${
              visibleBenefits?.includes(benefit?.id) ?
              'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`
              }
              style={{ transitionDelay: `${index * 100}ms` }}>

                <div className={`p-6 rounded-xl border-2 transition-all duration-300 ${
              activeBenefit === index ?
              `border-${benefit?.color} bg-${benefit?.color}/5 shadow-lg` :
              'border-border hover:border-primary/30 hover:bg-muted/50'}`
              }>
                  <div className="flex items-start justify-end gap-4 text-right">
                    <div className="flex-1">
                      <h3 className={`text-xl font-noto-arabic font-bold mb-2 transition-colors ${
                    activeBenefit === index ? `text-${benefit?.color}` : 'text-text-primary'}`
                    }>
                        {benefit?.title}
                      </h3>
                      
                      <p className="text-text-secondary font-cairo mb-3">
                        {benefit?.shortDesc}
                      </p>
                      
                      {activeBenefit === index &&
                    <div className="space-y-3 animate-fade-in">
                          <p className="text-sm text-text-secondary font-cairo leading-relaxed">
                            {benefit?.detailedDesc}
                          </p>
                          
                          <div className="grid grid-cols-2 gap-2">
                            {benefit?.features?.map((feature, idx) =>
                        <div key={idx} className="flex items-center justify-end gap-2">
                                <span className="text-sm font-cairo text-text-secondary">{feature}</span>
                                <Icon name="Check" size={14} className={`text-${benefit?.color}`} />
                              </div>
                        )}
                          </div>
                        </div>
                    }
                    </div>
                    
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                  activeBenefit === index ?
                  `bg-${benefit?.color}/10 scale-110` :
                  'bg-muted'}`
                  }>
                      <Icon
                      name={benefit?.icon}
                      size={28}
                      className={activeBenefit === index ? `text-${benefit?.color}` : 'text-text-secondary'} />

                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>*/}

          {/* Active Benefit Image */}
          <div className="lg:order-1">
            <div className="relative w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
              <Image
                src={benefits?.[activeBenefit]?.image}
                alt={benefits?.[activeBenefit]?.imageAlt}
                className="w-full h-full object-cover transition-all duration-700 ease-in-out" />

              
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              
              {/* Floating Info */}
              <div className="absolute bottom-6 right-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center justify-start gap-3">
                  <div className="text-right">
                    <h4 className="font-cairo font-bold text-text-primary text-right">
                      {benefits?.[activeBenefit]?.title}
                    </h4>
                    <p className="text-sm text-text-secondary text-right">
                      {benefits?.[activeBenefit]?.shortDesc}
                    </p>
                  </div>
                  <Icon
                    name={benefits?.[activeBenefit]?.icon}
                    size={24}
                    className={`text-${benefits?.[activeBenefit]?.color}`} />

                </div>
              </div>
            </div>

            {/* Progress Indicators */}
            <div className="flex justify-center gap-2 mt-6">
              {benefits?.map((_, index) =>
              <button
                key={index}
                onClick={() => handleBenefitClick(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                activeBenefit === index ?
                `bg-${benefits?.[index]?.color} scale-125` :
                'bg-border hover:bg-primary/50'}`
                } />

              )}
            </div>
          </div>
        </div>

        {/* Stats Section 
        <div className="grid md:grid-cols-3 gap-8 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-2xl p-8 border border-primary/20">
          <div className="text-center">
            <div className="text-3xl font-bold text-primary font-noto-arabic mb-2">
              ٥٠,٠٠٠+
            </div>
            <div className="text-text-secondary font-cairo">عميل راضٍ</div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-accent font-noto-arabic mb-2">
              ٤.٩/٥
            </div>
            <div className="text-text-secondary font-cairo">تقييم العملاء</div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-success font-noto-arabic mb-2">
              ٩٨%
            </div>
            <div className="text-text-secondary font-cairo">معدل الرضا</div>
          </div>
        </div>*/}
      </div>
    </section>);

};

export default BenefitsSection;